'use client'

import { useMemo } from 'react'
import { erc20Abi } from 'viem'
import { useReadContract } from 'wagmi'

import { cn } from '../../utils'
import { TOKEN_ADDRESSES } from '../constants/Tokens'
import { useIsHydrated } from '../hooks/useIsHydrated'
import { usePollRpc } from '../hooks/usePollRpc'
import { useSusdeYieldTooltipData } from '../hooks/useSusdeYieldTooltipData'
import { formatBigInt, NumberType } from '../utils/formatNumbers'

import { GradientDivider } from './GradientDivider'
import { Pill } from './Pill'
import { StatWithIcon } from './StatWithIcon'
import { TvlBreakdownTooltipContent } from './TvlBreakdownTooltipContent'
import { YieldTooltip } from './YieldTooltip'

interface StatsProps {
  isIdle?: boolean
  isMobileMenuStats?: boolean
}

export const Stats = ({ isIdle, isMobileMenuStats = false }: StatsProps) => {
  const { data: usdeTvlData, queryKey: usdeTvlQueryKey } = useReadContract({
    abi: erc20Abi,
    address: TOKEN_ADDRESSES.USDe,
    chainId: 1,
    functionName: 'totalSupply',
  })

  const { data: usdtbTvlData, queryKey: usdtbTvlQueryKey } = useReadContract({
    abi: erc20Abi,
    address: TOKEN_ADDRESSES.USDtb,
    chainId: 1,
    functionName: 'totalSupply',
  })

  usePollRpc({ interval: 20, isIdle, queryKey: [usdeTvlQueryKey, usdtbTvlQueryKey] })

  const { tooltip, yieldApy, yieldStr } = useSusdeYieldTooltipData()

  const isHydrated = useIsHydrated()

  const tvl = useMemo(() => {
    if (!isHydrated || !usdeTvlData || !usdtbTvlData) return undefined
    return formatBigInt(usdeTvlData + usdtbTvlData, 'USDe', NumberType.FiatTokenAmt)
  }, [isHydrated, usdeTvlData, usdtbTvlData])

  return (
    <Pill singleBorder>
      <div className="flex flex-wrap xs:gap-1.5 gap-0 sm:gap-2.5">
        <StatWithIcon
          imgSrc="/shared/usde.svg"
          label="TVL"
          value={tvl}
          tooltipWidth="w-[170px]"
          tooltip={<TvlBreakdownTooltipContent usdeTvlData={usdeTvlData} usdtbTvlData={usdtbTvlData} />}
          classes={cn(isMobileMenuStats ? 'pl-0 md:pl-0' : 'hidden lg:flex')}
        />

        <GradientDivider
          variant="vertical"
          height="h-[20px]"
          className={cn(isMobileMenuStats ? 'hidden sm:flex' : 'hidden lg:flex')}
        />
        <StatWithIcon
          imgSrc="/shared/sUSDe.svg"
          label="APY"
          value={yieldStr}
          tooltip={<YieldTooltip yieldStr={yieldApy} tooltip={tooltip} />}
          imgClasses="flex"
          classes={cn(isMobileMenuStats ? 'hidden sm:flex' : 'sm:flex')}
        />
      </div>
    </Pill>
  )
}
